/**
 * Blog Index Page
 * Lists all SEO blog posts. Public route — no auth required.
 */

import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';

const Blog = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Blog | WordExPress — AI WordPress Theme Generator';
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb' }}>
      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 100%)',
          py: { xs: 8, md: 10 },
          mb: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 3, fontSize: '0.75rem' }}
          >
            WordExPress Blog
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: '#fff',
              mt: 1,
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            WordPress, Themes &amp; Building in Public
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 400, maxWidth: 560, mx: 'auto' }}
          >
            Honest breakdowns, how-tos, and behind-the-scenes posts from the team building WordExPress.
          </Typography>
        </Container>
      </Box>

      {/* Post Grid */}
      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Grid container spacing={4}>
          {blogPosts.map((post) => (
            <Grid item xs={12} md={6} lg={4} key={post.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 3,
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(107, 70, 193, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 3 }}
                >
                  <CardContent sx={{ p: 0, width: '100%', flexGrow: 1 }}>
                    {/* Meta row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={post.date}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(107, 70, 193, 0.08)',
                          color: '#6B46C1',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <AccessTimeIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{post.readTime}</Typography>
                      </Box>
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      gutterBottom
                      sx={{ lineHeight: 1.35, color: '#1a1a2e' }}
                    >
                      {post.title}
                    </Typography>

                    {/* Excerpt */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7, mt: 1 }}
                    >
                      {post.excerpt}
                    </Typography>
                  </CardContent>

                  {/* Read More */}
                  <Box sx={{ mt: 3 }}>
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        color: '#6B46C1',
                        fontWeight: 700,
                        p: 0,
                        '&:hover': { background: 'transparent', color: '#8B5CF6' },
                      }}
                    >
                      Read More
                    </Button>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box
          sx={{
            mt: 10,
            p: { xs: 4, md: 6 },
            background: 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 100%)',
            borderRadius: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" fontWeight={800} sx={{ color: '#fff', mb: 1 }}>
            Ready to generate your theme?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
            Describe your site and get a complete WordPress theme in 5 minutes.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://getwordexpress.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: '#fff',
              color: '#6B46C1',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            Try WordExPress Free
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Blog;
